import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoGrid from "@/components/feature/VideoGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import EmptyState from "@/components/base/EmptyState";
import ShareBar from "@/pages/detail/components/ShareBar";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  getVideoById,
  getRelatedVideos,
  getTypeName,
  formatViews,
  getVideosByType,
} from "@/utils/videoFilters";

export default function DetailPage() {
  const { id = "" } = useParams();
  const video = useMemo(() => getVideoById(id), [id]);
  const [toast, setToast] = useState(false);

  useDocumentTitle(
    video ? `${video.title} - 99视频` : "内容详情 - 99视频",
    video
      ? `${video.title}是一部${video.year}年${video.region}${getTypeName(video.type)}，${video.description.slice(0, 60)}`
      : "99视频内容详情页"
  );

  if (!video) {
    return (
      <div className="w-full max-w-page mx-auto px-4 md:px-6 py-20">
        <EmptyState
          icon="ri-file-unknow-line"
          title="内容不存在或已下架"
          description="该影视资源可能已下线或链接有误，返回首页发现更多精彩内容。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const related = getRelatedVideos(video, 5);
  const typeVideos = getVideosByType(video.type).filter((v) => v.id !== video.id);
  const recommend = (related.length >= 5 ? related : [...related, ...typeVideos]).slice(0, 5);

  const handlePlay = () => {
    setToast(true);
    window.setTimeout(() => setToast(false), 2400);
  };

  const infoRows = [
    { label: "类型", value: video.tags.join(" / ") },
    { label: "地区", value: video.region },
    { label: "年份", value: video.year },
    { label: "时长", value: video.duration },
    { label: "导演", value: video.director },
    { label: "主演", value: video.cast.join("、") },
  ];

  return (
    <div className="w-full">
      <section className="relative w-full bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={video.backdrop}
            alt={`${video.title}剧照`}
            title={`${video.title} - 99视频高清影视`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/80 to-foreground-950/60"></div>
        </div>

        <div className="relative w-full max-w-page mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="[&_a]:text-background-200/80 [&_span]:text-background-50">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: getTypeName(video.type), to: `/category/${video.type}` },
                { label: video.title },
              ]}
            />
          </div>

          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 sm:w-48 md:w-56 shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-800">
                <img
                  src={video.poster}
                  alt={`${video.title}封面海报`}
                  title={`${video.title} - 99视频高清影视`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-4xl font-extrabold text-background-50">
                {video.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
                <span className="inline-flex items-center gap-1 text-accent-400 font-semibold text-base">
                  <i className="ri-star-fill"></i>
                  {video.rating.toFixed(1)}
                </span>
                <span>{video.year}</span>
                <span>{video.region}</span>
                <span>{video.duration}</span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-play-circle-line"></i>
                  {formatViews(video.views)} 播放
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {video.tags.map((t) => (
                  <Link
                    key={t}
                    to={`/search?q=${encodeURIComponent(t)}`}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-background-50/12 text-background-100 hover:bg-accent-500 hover:text-accent-950 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t}
                  </Link>
                ))}
              </div>

              <p className="mt-4 text-sm md:text-base text-background-200/90 leading-relaxed max-w-3xl">
                {video.description}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handlePlay}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-accent-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即播放
                </button>
                <div className="[&_span]:text-background-200 [&_button]:bg-background-50/12 [&_button]:text-background-100">
                  <ShareBar title={video.title} />
                </div>
              </div>

              {toast && (
                <div className="mt-4 inline-block px-4 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm animate-fade-in">
                  本站为演示平台，播放功能暂未接入真实接口
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full max-w-page mx-auto px-4 md:px-6 py-10 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold text-foreground-950 mb-4">
              内容简介
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              {video.description}
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              《{video.title}》由{video.director}执导，{video.cast.join("、")}等联袂出演，是一部{video.year}年出品的{video.region}
              {getTypeName(video.type)}作品。作品以细腻的叙事与出色的画面呈现，展现了{video.tags.join("、")}等元素融合的独特魅力。
            </p>
          </div>

          <div>
            <h2 className="text-xl font-bold text-foreground-950 mb-4">
              基本信息
            </h2>
            <dl className="rounded-lg bg-background-100 border border-background-200 divide-y divide-background-200">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-4 px-4 py-3">
                  <dt className="w-16 shrink-0 text-sm text-foreground-500">
                    {row.label}
                  </dt>
                  <dd className="text-sm text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-t border-background-200">
        <div className="w-full max-w-page mx-auto px-4 md:px-6 py-12 md:py-14">
          <SectionHeading
            icon="ri-movie-line"
            title="相关推荐"
            subtitle="与当前作品类型或标签相近的影视资源"
            moreLabel={`更多${getTypeName(video.type)}`}
            moreTo={`/category/${video.type}`}
          />
          <VideoGrid videos={recommend} emptyTitle="暂无相关推荐" />
        </div>
      </section>
    </div>
  );
}